import { useDispatch, useSelector } from 'react-redux'
import { selectPrimarySecurity } from '../../../../selectors/home'
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Menu, MenuItem } from '@mui/material'
import { SearchParamsContext } from '../../../../components/providers/searchParamsProvider'
import AnalyticsManager from '../../../../analytics/AnalyticsManager'
import { ExtraSmallButton } from '../../../../components/custom'
import { createStringDateFromStamp } from '../../../../util/tradePage'
import { selectMultiStrikeChartConfigExpiry } from '../../../../selectors/chart'
import { ChartViewKey } from '../../../../assets/constants'
import { updateMultiStrikeChartConfig } from '../../../../reducer/chart.reducer'
import { selectUnderlyingOptionsExpiriesV2 } from '../../../../selectors/options'
import { DropDownExpiries } from '../../../../reducer/options.reducer'
import { useWidgetSettingsMenuProps } from '../../../../components/widgetDashboard/widgetSettingsMenuContext'

type ExpiryMenuBodyProps = {
    currentExpiry: string
    optionsExpiries: DropDownExpiries[]
    onSelectExpiry: (value: string) => void
    fullWidth?: boolean
}

const ExpiryMenuBody = memo(({ currentExpiry, optionsExpiries, onSelectExpiry, fullWidth = false }: ExpiryMenuBodyProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
    const menuProps = useWidgetSettingsMenuProps('center')

    const expiryNameToFullName = useCallback(
        (ex: string) => {
            const index = optionsExpiries.findIndex((o) => o.expiry.name === ex)
            if (index >= 0) {
                return createStringDateFromStamp(optionsExpiries[index].expiry?.expiry ?? 0)
            }
            return ex
        },
        [optionsExpiries]
    )

    if (!currentExpiry || !optionsExpiries.length) return null

    const content = (
        <>
            <ExtraSmallButton
                color="inherit"
                id="multistrike-expiry-menu-button"
                variant="outlined"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-haspopup="true"
                sx={{
                    height: '32px',
                    width: fullWidth ? '100%' : '80px',
                    borderColor: (theme) => theme.palette.background.default,
                    textWrap: 'nowrap',
                }}
            >
                {expiryNameToFullName(currentExpiry)}
            </ExtraSmallButton>
            <Menu
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(undefined)}
                {...menuProps}
                sx={{ ...menuProps.sx, '&.MuiInputBase-root': { height: '100%' } }}
            >
                <div id="multistrike-expiry-menu">
                    {optionsExpiries.map((o) => (
                        <MenuItem
                            key={o.expiry.name}
                            selected={currentExpiry === o.expiry.name}
                            onClick={() => {
                                onSelectExpiry(o.expiry.name)
                                setAnchorEl(undefined)
                            }}
                        >
                            {expiryNameToFullName(o.expiry.name)}
                        </MenuItem>
                    ))}
                </div>
            </Menu>
        </>
    )

    return fullWidth ? <Box sx={{ width: '100%' }}>{content}</Box> : content
})

ExpiryMenuBody.displayName = 'ExpiryMenuBody'

const MultiStrikesExpiryMenuPage = memo(({ fullWidth }: { fullWidth?: boolean }) => {
    const { searchParams, setSearchParams } = useContext(SearchParamsContext)!
    const { key } = useSelector(selectPrimarySecurity)
    const selectExpiries = useMemo(() => selectUnderlyingOptionsExpiriesV2(key), [key])
    const optionsExpiries = useSelector(selectExpiries)
    const urlExpiry = decodeURIComponent(searchParams.get('expiry') || '')
    const setFirstRenderExpiry = useRef(false)

    useEffect(() => {
        if (setFirstRenderExpiry.current) return
        if (urlExpiry) {
            setFirstRenderExpiry.current = true
            return
        }
        const defaultExpiry = optionsExpiries?.[0]?.expiry?.name
        if (defaultExpiry) {
            searchParams.set('expiry', defaultExpiry)
            setSearchParams(searchParams, { replace: true })
            setFirstRenderExpiry.current = true
        }
    }, [optionsExpiries, searchParams, setSearchParams, urlExpiry])

    const handleOptionExpiryChange = useCallback(
        (value: string) => {
            searchParams.set('expiry', value)
            setSearchParams(searchParams)
            AnalyticsManager.logEvent('Expiry Selected', { selected_value: value, source: 'multistrike' })
        },
        [searchParams, setSearchParams]
    )

    if (!optionsExpiries?.length) return null

    return (
        <ExpiryMenuBody
            currentExpiry={urlExpiry}
            optionsExpiries={optionsExpiries}
            onSelectExpiry={handleOptionExpiryChange}
            fullWidth={fullWidth}
        />
    )
})

MultiStrikesExpiryMenuPage.displayName = 'MultiStrikesExpiryMenuPage'

const MultiStrikesExpiryMenuWidget = memo(({ viewKey, fullWidth }: { viewKey: string; fullWidth?: boolean }) => {
    const dispatch = useDispatch()
    const { key } = useSelector(selectPrimarySecurity)
    const selectExpiries = useMemo(() => selectUnderlyingOptionsExpiriesV2(key), [key])
    const optionsExpiries = useSelector(selectExpiries)
    const selectConfigExpiry = useMemo(() => selectMultiStrikeChartConfigExpiry(viewKey), [viewKey])
    const expiry = useSelector(selectConfigExpiry)

    const handleOptionExpiryChange = useCallback(
        (value: string) => {
            dispatch(updateMultiStrikeChartConfig({ viewKey, config: { expiry: value } }))
            AnalyticsManager.logEvent('Expiry Selected', { selected_value: value, source: 'multistrike' })
        },
        [dispatch, viewKey]
    )

    if (!expiry || !optionsExpiries?.length) return null

    return (
        <ExpiryMenuBody
            currentExpiry={expiry}
            optionsExpiries={optionsExpiries}
            onSelectExpiry={handleOptionExpiryChange}
            fullWidth={fullWidth}
        />
    )
})

MultiStrikesExpiryMenuWidget.displayName = 'MultiStrikesExpiryMenuWidget'

const MultiStrikesExpiryMenu = ({ viewKey, fullWidth }: { viewKey: string; fullWidth?: boolean }) => {
    const isWidget = viewKey !== ChartViewKey.MULTI_STRIKES_CHART
    if (isWidget) return <MultiStrikesExpiryMenuWidget viewKey={viewKey} fullWidth={fullWidth} />
    return <MultiStrikesExpiryMenuPage fullWidth={fullWidth} />
}

export default memo(MultiStrikesExpiryMenu)
